import { format, isToday } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  AttachmentType,
  SUPPORT_STATUS_LABEL,
  SupportRequestAttachment,
  SupportRequestHistory,
  SupportRequestHistoryAction,
} from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import { cn } from "@/app/utils/cn";
import { handleDownload } from "@/app/utils/download";
import { withCdn } from "@/app/utils/withCdn";

interface SupportHistoryProps {
  history?: SupportRequestHistory[];
}

const formatDate = (date: string) => {
  const parsed = new Date(date);
  const time = format(parsed, "HH:mm", { locale: ptBR });
  return isToday(parsed)
    ? `hoje · ${time}`
    : `${format(parsed, "dd/MM", { locale: ptBR })} · ${time}`;
};

const actorLabel = (entry: SupportRequestHistory, currentUserId?: string) => {
  if (currentUserId && entry.actor?.id === currentUserId) return "Você";
  return entry.actor?.name || "Sistema";
};

const entryDescription = (entry: SupportRequestHistory) => {
  if (entry.action === SupportRequestHistoryAction.CLAIM_TICKET) {
    return 'Chamado atribuído via "Atender próximo"';
  }

  if (entry.action === SupportRequestHistoryAction.ASSIGN_TICKET) {
    return entry.message || "Chamado reatribuído";
  }

  if (
    entry.action === SupportRequestHistoryAction.UPDATE_STATUS &&
    !entry.from &&
    entry.to
  ) {
    return "Chamado aberto";
  }

  if (entry.from && entry.to && entry.from !== entry.to) {
    return `${SUPPORT_STATUS_LABEL[entry.from]} → ${SUPPORT_STATUS_LABEL[entry.to]}`;
  }

  if (entry.action === SupportRequestHistoryAction.SELLER_REPLY) {
    return "Resposta do vendedor";
  }

  return null;
};

const byNewest = (history?: SupportRequestHistory[]) =>
  [...(history ?? [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

export const SupportHistory = ({ history }: SupportHistoryProps) => {
  const { user } = useAuth();

  const entries = byNewest(history).filter(
    (entry) =>
      !!entryDescription(entry) ||
      !!entry.message?.trim() ||
      !!entry.attachments?.length,
  );

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Histórico
      </p>

      {entries.length === 0 ? (
        <p className="text-sm text-black04">Nenhuma movimentação ainda.</p>
      ) : (
        <ol className="flex flex-col">
          {entries.map((entry, index) => (
            <HistoryEntry
              key={`${entry.date}-${entry.action}-${index}`}
              entry={entry}
              currentUserId={user?._id}
              isFirst={index === 0}
              isLast={index === entries.length - 1}
            />
          ))}
        </ol>
      )}
    </div>
  );
};

interface HistoryEntryProps {
  entry: SupportRequestHistory;
  currentUserId?: string;
  isFirst: boolean;
  isLast: boolean;
}

const HistoryEntry = ({
  entry,
  currentUserId,
  isFirst,
  isLast,
}: HistoryEntryProps) => {
  const title = entryDescription(entry);
  const message = entry.message?.trim();
  const showMessage =
    !!message && entry.action !== SupportRequestHistoryAction.ASSIGN_TICKET;

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {!isLast && (
        <span className="absolute bottom-0 left-[3px] top-3 w-px bg-gray01" />
      )}

      <span
        className={cn(
          "relative z-10 mt-1.5 size-[7px] shrink-0 rounded-full border border-black03 bg-white",
          isFirst && "bg-black03",
        )}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title && <p className="font-semibold text-black03">{title}</p>}

        {showMessage &&
          (title ? (
            <p className="whitespace-break-spaces text-black03">
              &ldquo;{message}&rdquo;
            </p>
          ) : (
            <p className="whitespace-break-spaces font-semibold text-black03">
              {message}
            </p>
          ))}

        {!!entry.attachments?.length && (
          <div className="mt-1 flex flex-wrap gap-2">
            {entry.attachments.map((attachment, index) => (
              <EntryAttachment
                key={`${attachment.url}-${index}`}
                attachment={attachment}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-black04">
          {actorLabel(entry, currentUserId)} · {formatDate(entry.date)}
        </p>
      </div>
    </li>
  );
};

const EntryAttachment = ({
  attachment,
}: {
  attachment: SupportRequestAttachment;
}) => (
  <button
    type="button"
    title={`Baixar ${attachment.name}`}
    onClick={() =>
      handleDownload({ url: attachment.url, name: attachment.name })
    }
  >
    {attachment.type === AttachmentType.IMAGE ? (
      <img
        src={withCdn(attachment.url)}
        alt={attachment.name}
        loading="lazy"
        className="size-14 rounded-md border border-gray01 bg-gray02 object-cover"
      />
    ) : (
      <video
        src={withCdn(attachment.url)}
        preload="metadata"
        className="h-14 w-20 rounded-md border border-gray01 object-cover"
      />
    )}
  </button>
);
